/**
 * handling strings and Date JS objects in Europe/Vienna (Europe/Vienna, CE(S)T)
 */

import { DateTime } from 'luxon';

export default {
  /**
   * Checks if the date string is valid by trying to parse it as a Luxon DateTime object.
   * @param dateStringOrDateJs {string}
   * @returns {boolean} True if valid, false otherwise.
   */
  isDateValid(dateStringOrDateJs) {
    try {
      // First, try parsing the date as an ISO string
      let dateTime = DateTime.fromISO(dateStringOrDateJs, { zone: 'Europe/Paris' });
      if (!dateTime.isValid) {
        // If ISO parsing fails, try parsing it as a JavaScript Date object
        dateTime = DateTime.fromJSDate(new Date(dateStringOrDateJs), { zone: 'Europe/Paris' });
      }
      if (!dateTime.isValid) {
        dateTime = DateTime.fromFormat(dateStringOrDateJs, 'yyyy-M-d', { zone: 'Europe/Paris' });
      }

      return dateTime.isValid;
    } catch (e) {
      return false;
    }
  },

  /**
   * Formats a date string to 'HH:mm:ss dd.MM.yyyy' using Luxon in CET time zone.
   * Sets time to 00:00:00.000 if the date is valid.
   * @param dateStringOrDateJs {string}
   * @returns {string} Formatted date string or an empty string if invalid.
   */
  formatDateTime(dateStringOrDateJs) {
    if (!this.isDateValid(dateStringOrDateJs))
      return "";

    // Try parsing as an ISO string first, then fallback to parsing as a JavaScript Date object
    let dateTime = DateTime.fromISO(dateStringOrDateJs, { zone: 'Europe/Paris' });
    if (!dateTime.isValid) {
      dateTime = DateTime.fromJSDate(new Date(dateStringOrDateJs), { zone: 'Europe/Paris' });
    }
    if (!dateTime.isValid) {
      dateTime = DateTime.fromFormat(dateStringOrDateJs, 'yyyy-M-d', { zone: 'Europe/Paris' });
    }

    // Ensure time is set to 00:00:00.000 and format the date and time using Luxon
    dateTime = dateTime.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
    return dateTime.toFormat('HH:mm:ss dd.MM.yyyy');
  },

  /**
   * Formats a date string to 'dd.MM.yyyy' using Luxon in CET time zone.
   * Sets time to 00:00:00.000 if the date is valid.
   * @param dateStringOrDateJs {string}
   * @returns {string} Formatted date string or an empty string if invalid.
   */

  formatDate(dateStringOrDateJs) {
    if (!this.isDateValid(dateStringOrDateJs))
      return ""; // Returning "" for testing

    // Try parsing as an ISO string first, then fallback to parsing as a JavaScript Date object
    let dateTime = DateTime.fromISO(dateStringOrDateJs, { zone: 'Europe/Paris' });
    if (!dateTime.isValid) {
      dateTime = DateTime.fromJSDate(new Date(dateStringOrDateJs), { zone: 'Europe/Paris' });
    }
    if (!dateTime.isValid) {
      dateTime = DateTime.fromFormat(dateStringOrDateJs, 'yyyy-M-d', { zone: 'Europe/Paris' });
    }

    // Set the time to 00:00:00.000 and format the date using Luxon
    return dateTime.set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
      .toFormat('dd.MM.yyyy');
  },

  /**
   * Formats a date string to 'YYYY/mm/dd' using Luxon in CET time zone.
   * Sets time to 00:00:00.000 if the date is valid.
   * @param dateStringOrDateJs {string}
   * @returns {string} Formatted date string or an empty string if invalid.
   */
  formatDateForEnergyDataService(dateStringOrDateJs) {
    if (!this.isDateValid(dateStringOrDateJs)) {
      return "";
    }

    // Try parsing as an ISO string first, then fallback to parsing as a JavaScript Date object
    let dateTime = DateTime.fromISO(dateStringOrDateJs, { zone: 'Europe/Paris' });
    if (!dateTime.isValid) {
      dateTime = DateTime.fromJSDate(new Date(dateStringOrDateJs), { zone: 'Europe/Paris' });
    }
    if (!dateTime.isValid) {
      dateTime = DateTime.fromFormat(dateStringOrDateJs, 'yyyy-M-d', { zone: 'Europe/Paris' });
    }

    // Set the time to 00:00:00.000 and format the date using Luxon to "YYYY/mm/dd"
    return dateTime.set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
        .toFormat('yyyy/MM/dd'); // Note: use 'yyyy' for the four-digit year
},

  /**
   * Parses a date string in 'dd.MM.yyyy' format using Luxon in CET time zone.
   * Sets time to 00:00:00.000 if the date is valid.
   * @param inputDateString {string} Date string in 'dd.MM.yyyy' format
   * @returns {DateTime} Luxon DateTime object with time set to 00:00:00.000
   */
    parseDate(inputDateString) {
    return DateTime.fromFormat(inputDateString, 'dd.MM.yyyy', { zone: 'Europe/Paris' })
      .set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
  }
}
